import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  MapPin,
  Plus,
  Edit2,
  Phone,
  User,
  Trash2,
  Loader2,
} from "lucide-react";

import {
  useCreateAddressMutation,
  useLazyDeleteAddressByIdQuery,
  useLazyGetAddressByIdQuery,
  useLazyGetAddressQuery,
  useUpdateAddressMutation,
} from "../../redux/actions/Customer/profileCustomer";

import { IAddressData } from "../../types/Response/CustomerResponse";
import { useAlert } from "../customer/AlertContext";
import { IAddressRequest } from "../../types/Request/CustomerRequest";
import { MAPS_KEY } from "../../config/apiUrlsCustomer";
import Autocomplete from "react-google-autocomplete";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
} from "@react-google-maps/api";

import Dropdown from "../customer/Dropdown";
import TextInputComp from "../customer/TextInputComp";
import { countryCodes } from "../../utils/Customer/CustomerData";
import { RootState } from "../../redux/rootReducer";
import { useSelector } from "react-redux";

export default function AddressManager() {
  const [currentView, setCurrentView] =
    useState<"list" | "form">("list");

  const [selectedAddressId, setSelectedAddressId] =
    useState<number | undefined>(undefined);

  const [mockAddresses, setMockAddresses] =
    useState<IAddressData[]>([]);

  const [
    triggerFetchAddress,
    { isFetching: isListLoading },
  ] = useLazyGetAddressQuery();

  const [deleteAddress] =
    useLazyDeleteAddressByIdQuery();

  const { showConfirm } = useAlert();

  const [updateAddress] =
    useUpdateAddressMutation();

  const fetchApi = async () => {
    try {
      const response =
        await triggerFetchAddress().unwrap();

      const addressData =
        response?.data || response || [];

      setMockAddresses(
        Array.isArray(addressData)
          ? addressData
          : []
      );
    } catch (error) {
      console.error(
        "Failed to fetch address list:",
        error
      );
    }
  };

  useEffect(() => {
    fetchApi();
  }, [currentView]);

  const handleAddNew = () => {
    setSelectedAddressId(undefined);
    setCurrentView("form");
  };

  const handleEdit = (id: number) => {
    setSelectedAddressId(id);
    setCurrentView("form");
  };

  const handleMakeDefault = async (
    address: IAddressData
  ) => {
    try {
      const payload: IAddressRequest = {
        fullName: address.fullName,
        email: address.email,
        phoneNumber: address.phoneNumber,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        country: address.country,
        postalCode: address.postalCode,
        latitude: Number(address.latitude),
        longitude: Number(address.longitude),
        addressType: address.addressType,
        isDefault: true,
      };

      await updateAddress({
        addressId: address.id,
        data: payload,
      }).unwrap();

      fetchApi();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteClick = (id: number) => {
    showConfirm({
      title: "Delete Address",
      message:
        "Are you sure you want to remove this address permanently?",
      type: "warning",
      showCancel: true,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          const response =
            await deleteAddress(id).unwrap();

          if (response?.success) {
            showConfirm({
              title: "Success",
              message:
                response?.message ||
                "Address deleted successfully",
              type: "success",
              confirmText: "OK",
            });

            fetchApi();
          }
        } catch (error: any) {
          console.error(
            "Delete Api Error:",
            error
          );
        }
      },
    });
  };

  const handleFormSuccess = () => {
    setCurrentView("list");
    setSelectedAddressId(undefined);
  };

  return (
    <div className="min-h-screen p-4 rounded-2xl border border-[#DDE7D8] shadow-xs text-xs text-left">
      {currentView === "list" ? (
        <div className="max-w-5xl mx-auto">

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5 pb-3 border-b border-[#DDE7D8]">
            <div>
              <h1 className="text-lg sm:text-xl font-black text-[#2F5E2E] uppercase tracking-wider">
                My Addresses
              </h1>

              <p className="text-slate-400 text-xs sm:text-[13px] font-medium mt-1">
                Manage your saved billing and shipping locations
              </p>
            </div>

            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-1.5 px-4 h-9 bg-[#2F5E2E] text-white text-xs font-bold rounded-lg hover:bg-[#1f3f1e] transition shadow-sm w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              Add Address
            </button>
          </div>

          {isListLoading ? (
            <div className="flex flex-col justify-center items-center py-16">
              <Loader2 className="animate-spin h-7 w-7 text-[#2F5E2E]" />
              <p className="mt-2 text-xs text-slate-400 font-medium">
                Loading addresses...
              </p>
            </div>
          ) : mockAddresses.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-10 bg-white rounded-2xl border border-dashed border-[#DDE7D8] text-center">
              <div className="w-14 h-14 rounded-full bg-[#F1F7EF] flex items-center justify-center mb-3">
                <MapPin className="w-7 h-7 text-[#6DBE45]" />
              </div>

              <p className="text-gray-700 font-bold text-sm">
                No Saved Addresses
              </p>

              <p className="text-gray-400 text-xs mt-1">
                Add your delivery address to continue.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">

              {mockAddresses.map((address) => (
                <div
                  key={address.id}
                  className="p-5 sm:p-6 bg-white rounded-2xl border border-[#DDE7D8] flex flex-col justify-between hover:border-[#6DBE45] hover:shadow-md transition-all duration-200 min-h-[210px]"
                >
                  <div className="space-y-2">

                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 font-bold text-gray-900 text-sm min-w-0">
                        <div className="w-8 h-8 rounded-full bg-[#F1F7EF] flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-[#2F5E2E]" />
                        </div>

                        <span className="truncate">
                          {address.fullName}
                        </span>
                      </div>

                      {address.isDefault ? (
                        <span className="shrink-0 text-[9px] bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full border border-emerald-200 font-bold uppercase">
                          Default
                        </span>
                      ) : (
                        <button
                          onClick={() =>
                            handleMakeDefault(address)
                          }
                          className="shrink-0 text-[11px] font-semibold text-[#2F5E2E] hover:underline"
                        >
                          Make Default
                        </button>
                      )}
                    </div>

                    <div className="flex items-start gap-2 mt-3">
                      <MapPin className="w-4 h-4 text-[#6DBE45] shrink-0 mt-0.5" />

                      <div className="min-w-0">
                        <p className="text-slate-600 leading-relaxed text-[13px] font-medium">
                          {address.addressLine1}
                        </p>

                        <p className="text-slate-400 font-medium text-xs mt-1">
                          {address.city}, UAE
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3 text-slate-500 font-semibold text-xs">
                      <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center">
                        <Phone className="w-3.5 h-3.5 text-slate-500" />
                      </div>

                      <span>
                        {address.phoneNumber}
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-gray-100 flex gap-2 justify-end">

                    <button
                      onClick={() =>
                        handleEdit(address.id)
                      }
                      className="flex items-center gap-1.5 text-xs font-bold text-[#2F5E2E] border border-[#DDE7D8] bg-white px-3 py-1.5 rounded-lg hover:bg-[#F5FAF3] transition"
                    >
                      <Edit2 className="w-3 h-3" />
                      Edit
                    </button>

                    <button
                      onClick={() =>
                        handleDeleteClick(address.id)
                      }
                      className="flex items-center gap-1.5 text-xs font-bold text-red-600 border border-red-100 bg-white px-3 py-1.5 rounded-lg hover:bg-red-50 transition"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>

                  </div>
                </div>
              ))}

            </div>
          )}
        </div>
      ) : (
        <AddressForm
          addressId={selectedAddressId}
          onCancel={() => setCurrentView("list")}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}

// ============================================================
// ADDRESS FORM
// ============================================================

interface AddressFormProps {
  addressId?: number;
  onCancel: () => void;
  onSuccess: () => void;
  onError?: (
    title: string,
    message: string
  ) => void;
  onGuestEmailVerificationRequired?: (
    email: string
  ) => void;
  onExistingAccount?: (
    message?: string
  ) => void;
}

const containerStyle = {
  width: "100%",
  height: "220px",
  minHeight: "220px",
  display: "block",
  borderRadius: "12px",
};

const defaultCenter = {
  lat: 25.2048,
  lng: 55.2708,
};

// ============================================================
// PHONE VALIDATION
// ============================================================

const validatePhoneNumber = (
  phoneNumber: string,
  countryCode: string
): string | null => {
  const phone = phoneNumber.replace(/\D/g, "");

  if (!phone) {
    return "Phone number is required";
  }

  switch (countryCode) {

    // UAE +971
    case "AE":
      if (!/^5\d{8}$/.test(phone)) {
        return "UAE mobile number must start with 5 and contain 9 digits";
      }
      break;

    // India +91
    case "IN":
      if (!/^[6-9]\d{9}$/.test(phone)) {
        return "Indian mobile number must start with 6, 7, 8 or 9 and contain 10 digits";
      }
      break;

    // USA +1
    case "US":

    // Canada +1
    case "CA":
      if (!/^[2-9]\d{9}$/.test(phone)) {
        return "Please enter a valid 10-digit mobile number";
      }
      break;

    // UK +44
    case "GB":
      if (!/^7\d{9}$/.test(phone)) {
        return "UK mobile number must start with 7 and contain 10 digits";
      }
      break;

    // Australia +61
    case "AU":
      if (!/^4\d{8}$/.test(phone)) {
        return "Australian mobile number must start with 4 and contain 9 digits";
      }
      break;

    // Saudi Arabia +966
    case "SA":
      if (!/^5\d{8}$/.test(phone)) {
        return "Saudi mobile number must start with 5 and contain 9 digits";
      }
      break;

    // Qatar +974
    case "QA":
      if (!/^[3-7]\d{7}$/.test(phone)) {
        return "Qatar mobile number must contain 8 digits";
      }
      break;

    // Kuwait +965
    case "KW":
      if (!/^[569]\d{7}$/.test(phone)) {
        return "Kuwait mobile number must contain 8 digits";
      }
      break;

    // Oman +968
    case "OM":
      if (!/^9\d{7}$/.test(phone)) {
        return "Oman mobile number must start with 9 and contain 8 digits";
      }
      break;

    // Bahrain +973
    case "BH":
      if (!/^[36]\d{7}$/.test(phone)) {
        return "Bahrain mobile number must contain 8 digits";
      }
      break;

    // Egypt +20
    case "EG":
      if (!/^1[0125]\d{8}$/.test(phone)) {
        return "Egypt mobile number must start with 10, 11, 12 or 15 and contain 10 digits";
      }
      break;

    // Pakistan +92
    case "PK":
      if (!/^3\d{9}$/.test(phone)) {
        return "Pakistan mobile number must start with 3 and contain 10 digits";
      }
      break;

    // Bangladesh +880
    case "BD":
      if (!/^1[3-9]\d{8}$/.test(phone)) {
        return "Bangladesh mobile number must start with 13-19 and contain 10 digits";
      }
      break;

    // Sri Lanka +94
    case "LK":
      if (!/^7\d{8}$/.test(phone)) {
        return "Sri Lankan mobile number must start with 7 and contain 9 digits";
      }
      break;

    // Nepal +977
    case "NP":
      if (!/^9\d{9}$/.test(phone)) {
        return "Nepal mobile number must start with 9 and contain 10 digits";
      }
      break;

    // Bhutan +975
    case "BT":
      if (!/^17\d{6}$/.test(phone)) {
        return "Bhutan mobile number must start with 17 and contain 8 digits";
      }
      break;

    // Malaysia +60
    case "MY":
      if (!/^1\d{8,9}$/.test(phone)) {
        return "Malaysia mobile number must start with 1 and contain 9-10 digits";
      }
      break;

    // Singapore +65
    case "SG":
      if (!/^[89]\d{7}$/.test(phone)) {
        return "Singapore mobile number must start with 8 or 9 and contain 8 digits";
      }
      break;

    // Thailand +66
    case "TH":
      if (!/^0?[689]\d{8}$/.test(phone)) {
        return "Thailand mobile number must be valid";
      }
      break;

    default:
      if (!/^\d{7,15}$/.test(phone)) {
        return "Please enter a valid mobile number";
      }
  }

  return null;
};

// ============================================================
// ADDRESS FORM
// ============================================================

export function AddressForm({
  addressId,
  onCancel,
  onSuccess,
  onError,
  onGuestEmailVerificationRequired,
  onExistingAccount,
}: AddressFormProps) {

  const isEditMode = Boolean(addressId);

  const {
    isLoaded,
    loadError,
  } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MAPS_KEY,
    libraries: ["places"],
  });

  const [
    triggerFetchAddress,
    {
      data: fetchedData,
      isFetching,
    },
  ] = useLazyGetAddressByIdQuery();

  const [
    createAddress,
    {
      isLoading: isCreating,
    },
  ] = useCreateAddressMutation();

  const [
    updateAddress,
    {
      isLoading: isUpdating,
    },
  ] = useUpdateAddressMutation();

  const [selectedCode, setSelectedCode] =
    useState(countryCodes[0]);

  const [phone, setPhone] =
    useState("");

  const [phoneError, setPhoneError] =
    useState<string | null>(null);

  const customerType = useSelector(
    (state: RootState) =>
      state.auth.customer.customerType
  );

  const defaultFormValues: IAddressRequest = {
    fullName: "",
    email: "",
    phoneNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "United Arab Emirates",
    postalCode: "",
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    addressType: "Home",
    isDefault: true,
  };

  const [formValues, setFormValues] =
    useState<IAddressRequest>(
      defaultFormValues
    );

  const [errors, setErrors] = useState<
    Partial<Record<keyof IAddressRequest, string>>
  >({});

  const [isLocating, setIsLocating] =
    useState(false);

  const [
    mapInstance,
    setMapInstance,
  ] = useState<google.maps.Map | null>(null);

  const mapCenterCoordinates = useMemo(
    () => ({
      lat:
        Number(formValues.latitude) ||
        defaultCenter.lat,

      lng:
        Number(formValues.longitude) ||
        defaultCenter.lng,
    }),
    [
      formValues.latitude,
      formValues.longitude,
    ]
  );

  // ============================================================
  // RESET
  // ============================================================

  const resetForm = () => {
    setFormValues(defaultFormValues);
    setPhone("");
    setPhoneError(null);
    setSelectedCode(countryCodes[0]);
    setErrors({});
  };

  // ============================================================
  // FETCH ADDRESS
  // ============================================================

  useEffect(() => {
    if (isEditMode && addressId) {
      triggerFetchAddress(addressId);
    }
  }, [
    isEditMode,
    addressId,
    triggerFetchAddress,
  ]);

  useEffect(() => {
    if (fetchedData) {

      const address =
        "data" in fetchedData
          ? (fetchedData as any).data
          : fetchedData;

      let rawPhone =
        address.phoneNumber || "";

      if (rawPhone.startsWith("+971")) {

        setSelectedCode(
          countryCodes.find(
            (x) => x.value === "AE"
          ) || countryCodes[0]
        );

        rawPhone =
          rawPhone.replace("+971", "");

      } else if (rawPhone.startsWith("+91")) {

        setSelectedCode(
          countryCodes.find(
            (x) => x.value === "IN"
          ) || countryCodes[0]
        );

        rawPhone =
          rawPhone.replace("+91", "");

      } else {

        const matchedCountry =
          countryCodes.find((country) =>
            rawPhone.startsWith(
              country.label
            )
          );

        if (matchedCountry) {
          setSelectedCode(matchedCountry);

          rawPhone =
            rawPhone.replace(
              matchedCountry.label,
              ""
            );
        }
      }

      setPhone(
        rawPhone.replace(/\D/g, "")
      );

      setFormValues({
        fullName:
          address.fullName || "",

        email:
          address.email || "",

        phoneNumber:
          address.phoneNumber || "",

        addressLine1:
          address.addressLine1 || "",

        addressLine2:
          address.addressLine2 || "",

        city:
          address.city || "",

        state:
          address.state || "",

        country:
          address.country ||
          "United Arab Emirates",

        postalCode:
          address.postalCode || "",

        latitude:
          Number(address.latitude) ||
          defaultCenter.lat,

        longitude:
          Number(address.longitude) ||
          defaultCenter.lng,

        addressType:
          address.addressType ||
          "Home",

        isDefault:
          address.isDefault || false,
      });
    }
  }, [fetchedData]);

  // ============================================================
  // GOOGLE GEOCODING
  // ============================================================

  const fetchAddressFromCoords =
    async (
      lat: number,
      lng: number
    ) => {
      try {

        const response =
          await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`
          );

        const data =
          await response.json();

        if (
          data.status === "OK" &&
          data.results?.[0]
        ) {

          const place =
            data.results[0];

          const components =
            place.address_components || [];

          const getComponent =
            (type: string) =>
              components.find(
                (c: any) =>
                  c.types.includes(type)
              )?.long_name || "";

          setFormValues((prev) => ({
            ...prev,

            addressLine1:
              place.formatted_address ||
              prev.addressLine1,

            city:
              getComponent("locality") ||
              getComponent(
                "sublocality_level_1"
              ) ||
              "Dubai",

            state:
              getComponent(
                "administrative_area_level_1"
              ) || "Dubai",

            country:
              getComponent("country") ||
              "United Arab Emirates",

            postalCode:
              getComponent("postal_code") ||
              "00000",

            latitude: lat,
            longitude: lng,
          }));

          setErrors({});

          if (mapInstance) {
            mapInstance.panTo({
              lat,
              lng,
            });
          }
        }

      } catch (error) {
        console.error(
          "Geocoding failed:",
          error
        );
      }
    };

  // ============================================================
  // CURRENT LOCATION
  // ============================================================

  const handleUseCurrentLocation =
    () => {

      if (!navigator.geolocation) {
        alert(
          "Location features are not supported by this browser."
        );
        return;
      }

      setIsLocating(true);

      navigator.geolocation.getCurrentPosition(
        async (position) => {

          const lat =
            position.coords.latitude;

          const lng =
            position.coords.longitude;

          await fetchAddressFromCoords(
            lat,
            lng
          );

          setIsLocating(false);
        },
        () => {
          setIsLocating(false);

          alert(
            "Unable to find your current location."
          );
        }
      );
    };

  // ============================================================
  // MAP CLICK
  // ============================================================

  const onMapClick =
    useCallback(
      (
        e: google.maps.MapMouseEvent
      ) => {

        if (e.latLng) {

          const clickedLat =
            e.latLng.lat();

          const clickedLng =
            e.latLng.lng();

          fetchAddressFromCoords(
            clickedLat,
            clickedLng
          );
        }
      },
      [mapInstance]
    );

  const onMapLoad =
    useCallback(
      (map: google.maps.Map) => {
        setMapInstance(map);
      },
      []
    );

  const onMapUnmount =
    useCallback(() => {
      setMapInstance(null);
    }, []);

  // ============================================================
  // GOOGLE PLACE SELECTED
  // ============================================================

  const handlePlaceSelected =
    (place: any) => {

      if (!place?.address_components)
        return;

      const components =
        place.address_components;

      const getComponent =
        (type: string) =>
          components.find(
            (c: any) =>
              c.types.includes(type)
          )?.long_name || "";

      let lat =
        formValues.latitude;

      let lng =
        formValues.longitude;

      if (place.geometry?.location) {

        lat =
          typeof place.geometry.location.lat ===
            "function"
            ? place.geometry.location.lat()
            : place.geometry.location.lat;

        lng =
          typeof place.geometry.location.lng ===
            "function"
            ? place.geometry.location.lng()
            : place.geometry.location.lng;
      }

      const newLat =
        lat || defaultCenter.lat;

      const newLng =
        lng || defaultCenter.lng;

      setFormValues((prev) => ({
        ...prev,

        addressLine1:
          place.formatted_address ||
          prev.addressLine1,

        city:
          getComponent("locality") ||
          getComponent(
            "sublocality_level_1"
          ) ||
          "Dubai",

        state:
          getComponent(
            "administrative_area_level_1"
          ) || "Dubai",

        country:
          getComponent("country") ||
          "United Arab Emirates",

        postalCode:
          getComponent("postal_code") ||
          "00000",

        latitude: newLat,
        longitude: newLng,
      }));

      if (mapInstance) {
        mapInstance.panTo({
          lat: newLat,
          lng: newLng,
        });
      }

      setErrors({});
    };

  // ============================================================
  // FORM VALIDATION
  // ============================================================

  const validateForm =
    (): boolean => {

      const tempErrors:
        Partial<
          Record<
            keyof IAddressRequest,
            string
          >
        > = {};

      if (
        !formValues.fullName.trim()
      ) {
        tempErrors.fullName =
          "Full name is required";
      }

      if (
        !formValues.email?.trim()
      ) {

        tempErrors.email =
          "Email address is required";

      } else if (
        !/\S+@\S+\.\S+/.test(
          formValues.email
        )
      ) {

        tempErrors.email =
          "Please enter a valid email address";
      }

      if (
        !formValues.addressLine1.trim()
      ) {
        tempErrors.addressLine1 =
          "Please select a location";
      }

      if (
        !formValues.city.trim()
      ) {
        tempErrors.city =
          "City is required";
      }

      // COUNTRY-SPECIFIC PHONE VALIDATION
      const phoneValidationError =
        validatePhoneNumber(
          phone,
          selectedCode.value
        );

      setPhoneError(
        phoneValidationError
      );

      setErrors(tempErrors);

      return (
        Object.keys(tempErrors)
          .length === 0 &&
        !phoneValidationError
      );
    };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit =
    async (
      e: React.FormEvent
    ) => {

      e.preventDefault();

      if (!validateForm())
        return;

      try {

        const completePhoneString =
          `${selectedCode.label}${phone.trim()}`;

        const payload:
          IAddressRequest = {
          ...formValues,

          phoneNumber:
            completePhoneString,

          latitude:
            Number(
              formValues.latitude
            ),

          longitude:
            Number(
              formValues.longitude
            ),

          addressType:
            formValues.addressType,
        };

        // ======================================================
        // UPDATE
        // ======================================================

        if (
          isEditMode &&
          addressId
        ) {

          const response =
            await updateAddress({
              addressId,
              data: payload,
            }).unwrap();

          if (
            response?.success === false
          ) {
            return;
          }

          onSuccess();
          return;
        }

        // ======================================================
        // CREATE
        // ======================================================

        const response =
          await createAddress(
            payload
          ).unwrap();

        if (
          response?.success === false
        ) {
          return;
        }

        // ======================================================
        // CUSTOMER TYPE
        // ======================================================

        if (
          customerType ===
          "registered"
        ) {

          onSuccess();

        } else if (
          customerType ===
          "guest"
        ) {

          onGuestEmailVerificationRequired?.(
            formValues.email
              ?.trim()
              .toLowerCase() || ""
          );
        }

      } catch (err: any) {

        console.error(
          "Address save failed:",
          err
        );

        const status =
          err?.status;

        const message =
          err?.data?.Message ||
          err?.data?.message ||
          err?.data?.error ||
          err?.error ||
          err?.message ||
          "";

        // Existing account
        if (!isEditMode) {

          const isExistingAccount =
            status === 400 &&
            message
              .toLowerCase()
              .includes(
                "already associated"
              );

          if (isExistingAccount) {

            onExistingAccount?.(
              message ||
              "These details are already associated with an account. Please sign in to continue."
            );

            return;
          }
        }

        onError?.(
          isEditMode
            ? "Address Update Failed"
            : "Address Save Failed",

          message ||
          "Something went wrong while saving the address."
        );
      }
    };

  // ============================================================
  // LOADING
  // ============================================================

  if (isFetching) {

    return (
      <div className="flex flex-col justify-center items-center py-10 bg-white rounded-xl border">

        <Loader2 className="animate-spin h-5 w-5 text-[#2F5E2E]" />

        <span className="mt-2 text-xs text-[#2F5E2E] font-medium">
          Loading details...
        </span>

      </div>
    );
  }

  // ============================================================
  // MAP ERROR
  // ============================================================

  if (loadError) {

    return (
      <div className="text-xs text-red-500 font-bold p-4 text-center">
        Error loading Google Map scripts.
      </div>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="w-full mx-auto p-4 bg-white rounded-xl border border-slate-200/80 shadow-2xs text-xs text-left text-slate-700">

      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">

        <button
          type="button"
          onClick={() => {
            resetForm();
            onCancel();
          }}
          className="p-1 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-slate-500" />
        </button>

        <div>
          <h2 className="text-sm font-black text-slate-900 tracking-tight">
            {isEditMode
              ? "Modify Address"
              : "Delivery Location"}
          </h2>
        </div>

      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-3"
      >

        {/* ================================================== */}
        {/* MAP */}
        {/* ================================================== */}

        <div className="space-y-1">

 <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
  <span>
    Select your delivery location
  </span>

  <span className="hidden sm:block">
    Drag pin or tap map
  </span>
</div>
          <div className="rounded-xl overflow-hidden border border-slate-200 h-[200px] sm:h-[220px] bg-slate-50 relative shadow-sm block w-full">

            {isLoaded ? (

              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenterCoordinates}
                zoom={14}
                onLoad={onMapLoad}
                onUnmount={onMapUnmount}
                onClick={onMapClick}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  zoomControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_CENTER,
                  },
                  fullscreenControl: true,
                  fullscreenControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_TOP,
                  },
                  streetViewControl: false,
                  mapTypeControl: false,
                  clickableIcons: false,
                  gestureHandling: "greedy",
                  backgroundColor: "#f8fafc",
                }}
              >

                <MarkerF
                  position={mapCenterCoordinates}
                  draggable={true}
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                      <svg width="52" height="64" viewBox="0 0 52 64" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="3" stdDeviation="3" flood-opacity="0.25"/>
                          </filter>
                        </defs>
                        <path d="M26 2 C12.745 2 2 12.745 2 26 C2 42 26 62 26 62 C26 62 50 42 50 26 C50 12.745 39.255 2 26 2Z" fill="#2F5E2E" filter="url(#shadow)"/>
                        <circle cx="26" cy="25" r="14" fill="white"/>
                        <circle cx="26" cy="25" r="7" fill="#6DBE45"/>
                        <circle cx="26" cy="25" r="3" fill="#2F5E2E"/>
                      </svg>
                    `)}`,
                    scaledSize: new google.maps.Size(42, 52),
                    anchor: new google.maps.Point(21, 52),
                  }}
                  title="Delivery Location"
                  animation={google.maps.Animation.DROP}
                  onDragEnd={(e) => {
                    if (e.latLng) {
                      onMapClick({
                        latLng: e.latLng,
                      } as google.maps.MapMouseEvent);
                    }
                  }}
                />

              </GoogleMap>

            ) : (

              <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50">
                <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E] mb-2" />
                <span className="text-xs font-medium">
                  Loading Map...
                </span>
              </div>

            )}

         <button
  type="button"
  onClick={handleUseCurrentLocation}
  disabled={isLocating}
  className="
    absolute
    bottom-3
    left-1/2
    -translate-x-1/2
    z-10

    flex
    items-center
    justify-center
    gap-2

    px-4
    py-2.5

    rounded-full

    bg-white
    border
    border-[#DDE7D8]

    shadow-lg

    text-[#2F5E2E]
    text-xs
    font-bold

    hover:bg-[#F5FAF3]
    hover:border-[#6DBE45]

    active:scale-95

    transition-all

    disabled:opacity-60
    disabled:cursor-not-allowed

    whitespace-nowrap
  "
>
  <MapPin
    size={15}
    className="text-[#2F5E2E]"
  />

  <span>
    {isLocating
      ? "Finding Location..."
      : "Use Current Location"}
  </span>
</button>

          </div>
        </div>

        {/* ================================================== */}
        {/* AUTOCOMPLETE */}
        {/* ================================================== */}

        <div className="space-y-1">

          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider">
            Search Location *
          </label>

          <div className="relative flex items-center">

            <MapPin className="absolute left-3 w-3.5 h-3.5 text-slate-400 pointer-events-none z-10" />

            <Autocomplete
              apiKey={MAPS_KEY}
              className="w-full h-8 border border-slate-200 bg-slate-50/40 rounded-lg pl-8 pr-3 text-xs font-medium text-slate-800 outline-hidden focus:border-[#2F5E2E] transition shadow-inner"
              placeholder="Type address or select from map..."
              onPlaceSelected={
                handlePlaceSelected
              }
              defaultValue={
                formValues.addressLine1
              }
              key={
                formValues.addressLine1
              }
              options={{
                componentRestrictions: {
                  country: "ae",
                },
                types: [
                  "geocode",
                  "establishment",
                ],
              }}
            />

          </div>

          {errors.addressLine1 && (
            <p className="text-red-500 text-[10px] mt-0.5">
              {errors.addressLine1}
            </p>
          )}

        </div>

        {/* ================================================== */}
        {/* NAME + EMAIL */}
        {/* ================================================== */}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          <div className="space-y-1">

            <TextInputComp
              showLabel
              label="Full Name *"
              placeholder="Recipient Name"
              value={
                formValues.fullName
              }
              onChangeText={(text) =>
                setFormValues(
                  (prev) => ({
                    ...prev,
                    fullName: text,
                  })
                )
              }
              error={
                errors.fullName
              }
              className="w-full"
            />

          </div>

          <div className="space-y-1">

            <TextInputComp
              showLabel
              type="email"
              label="Email Address *"
              placeholder="Petland@example.com"
              value={
                formValues.email || ""
              }
              onChangeText={(text) =>
                setFormValues(
                  (prev) => ({
                    ...prev,
                    email: text,
                  })
                )
              }
              error={
                errors.email
              }
              className="w-full"
            />

          </div>

        </div>

        {/* ================================================== */}
        {/* PHONE */}
        {/* ================================================== */}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          <div className="space-y-1">

            <label className="text-[10px] font-bold uppercase text-slate-400">
              Phone Number *
            </label>

            <div className="flex items-start gap-2 w-full">

              {/* COUNTRY */}
              <div className="w-[90px] flex-shrink-0">

                <Dropdown
                  options={countryCodes}
                  value={selectedCode}
                  onChange={(option) => {

                    setSelectedCode(
                      option
                    );

                    setPhone("");

                    setPhoneError(
                      null
                    );
                  }}
                  iconPosition="right"
                  className="w-full"
                />

              </div>

              {/* PHONE */}
              <div className="flex-1 min-w-0">
                <TextInputComp
                  type="tel"
                  placeholder="Enter mobile number"
                  value={phone}
                  onChangeText={(text) => {
                    const numericText = text.replace(/\D/g, "");
                    setPhone(numericText);
                    setPhoneError(null);
                  }}
                  maxLength={
                    selectedCode.value === "AE" ||
                      selectedCode.value === "SA"
                      ? 9
                      : selectedCode.value === "IN" ||
                        selectedCode.value === "US" ||
                        selectedCode.value === "CA" ||
                        selectedCode.value === "GB" ||
                        selectedCode.value === "EG" ||
                        selectedCode.value === "PK" ||
                        selectedCode.value === "BD" ||
                        selectedCode.value === "NP"
                        ? 10
                        : selectedCode.value === "AU" ||
                          selectedCode.value === "LK"
                          ? 9
                          : 8
                  }
                  className="w-full"
                />

                {/* PHONE ERROR MESSAGE */}
                {phoneError && (
                  <p className="mt-1 text-[10px] font-medium text-red-500">
                    {phoneError}
                  </p>
                )}
              </div>
            </div>

          </div>

          {/* ================================================== */}
          {/* STREET */}
          {/* ================================================== */}

          <div className="space-y-1">

            <TextInputComp
              showLabel
              label="Street Address *"
              placeholder="Street address details"
              value={
                formValues.addressLine1
              }
              onChangeText={(text) =>
                setFormValues(
                  (prev) => ({
                    ...prev,
                    addressLine1:
                      text,
                  })
                )
              }
              error={
                errors.addressLine1
              }
              className="w-full"
            />

          </div>

        </div>

        {/* ================================================== */}
        {/* CITY + FLAT */}
        {/* ================================================== */}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          <div className="space-y-1">

            <TextInputComp
              showLabel
              label="City / Emirate *"
              placeholder="e.g. Dubai"
              value={
                formValues.city
              }
              onChangeText={(text) =>
                setFormValues(
                  (prev) => ({
                    ...prev,
                    city: text,
                  })
                )
              }
              error={
                errors.city
              }
              className="w-full"
            />

          </div>

          <div className="space-y-1">

            <TextInputComp
              showLabel
              label="Flat / Villa / Suite"
              placeholder="e.g. Apt 302"
              value={
                formValues.addressLine2 ||
                ""
              }
              onChangeText={(text) =>
                setFormValues(
                  (prev) => ({
                    ...prev,
                    addressLine2:
                      text,
                  })
                )
              }
              className="w-full"
            />

          </div>

        </div>

        {/* ================================================== */}
        {/* ADDRESS TYPE */}
        {/* ================================================== */}

        <div className="grid grid-cols-1 gap-3">

          <div className="space-y-1">

            <label className="text-[10px] font-bold uppercase text-slate-400">
              Address Label
            </label>

            <div className="flex items-center h-8 gap-4 pl-1">

              <label className="flex items-center gap-1 cursor-pointer font-bold text-slate-600">

                <input
                  type="radio"
                  name="addressType"
                  checked={
                    formValues.addressType ===
                    "Home"
                  }
                  onChange={() =>
                    setFormValues(
                      (p) => ({
                        ...p,
                        addressType:
                          "Home",
                      })
                    )
                  }
                  className="accent-[#2F5E2E] w-3 h-3"
                />

                <span>
                  Home
                </span>

              </label>

              <label className="flex items-center gap-1 cursor-pointer font-bold text-slate-600">

                <input
                  type="radio"
                  name="addressType"
                  checked={
                    formValues.addressType ===
                    "office"
                  }
                  onChange={() =>
                    setFormValues(
                      (p) => ({
                        ...p,
                        addressType:
                          "office",
                      })
                    )
                  }
                  className="accent-[#2F5E2E] w-3 h-3"
                />

                <span>
                  Office
                </span>

              </label>

            </div>

          </div>

        </div>

        {/* ================================================== */}
        {/* BUTTONS */}
        {/* ================================================== */}

        <div className="pt-2 flex gap-2 border-t border-slate-100">

          <button
            type="button"
            onClick={() => {
              resetForm();
              onCancel();
            }}
            className="w-1/3 h-8 border rounded-lg font-bold text-slate-500 hover:bg-slate-50 transition"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={
              isCreating ||
              isUpdating
            }
            className="w-2/3 h-8 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white font-black rounded-lg transition disabled:bg-slate-300"
          >
            {isCreating ||
              isUpdating
              ? "Saving..."
              : isEditMode
                ? "Update Address"
                : "Save Address"}
          </button>

        </div>

      </form>
    </div>
  );
}